import { Reveal, SectionHead } from "@/components/Reveal";
import { INDIA_PATH, toXY } from "@/components/indiaMap";

const CITIES = {
  hyderabad: { lon: 78.49, lat: 17.38 },
  delhi: { lon: 77.21, lat: 28.61 },
  mumbai: { lon: 72.88, lat: 19.08 },
  chennai: { lon: 80.27, lat: 13.08 },
  kolkata: { lon: 88.36, lat: 22.57 },
  jaipur: { lon: 75.79, lat: 26.91 },
  lucknow: { lon: 80.95, lat: 26.85 },
  ahmedabad: { lon: 72.57, lat: 23.02 },
  nagpur: { lon: 79.09, lat: 21.15 },
  bengaluru: { lon: 77.59, lat: 12.97 },
  vizag: { lon: 83.3, lat: 17.69 },
};

const ROUTES = [
  ["delhi", "jaipur", "ahmedabad", "mumbai"],
  ["delhi", "lucknow", "kolkata"],
  ["kolkata", "vizag", "chennai"],
  ["mumbai", "bengaluru", "chennai"],
  ["delhi", "nagpur", "hyderabad"],
  ["mumbai", "hyderabad"],
  ["hyderabad", "bengaluru"],
];

const pos = (k) => toXY(CITIES[k].lon, CITIES[k].lat);

const routePath = (route) => {
  const pts = route.map(pos);
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const k = (i % 2 === 0 ? 1 : -1) * len * 0.08;
    d += ` Q${mx - (dy / len) * k},${my + (dx / len) * k} ${b.x},${b.y}`;
  }
  return d;
};

const LABEL_LEFT = new Set(["mumbai"]);

const VEHICLES = [
  "Open Body 19–32 FT",
  "Container 20 / 40 FT",
  "Flatbed Trailer",
  "Low-Bed Trailer",
  "Semi Low-Bed",
  "Multi-Axle Trailer",
];

const HUBS = [
  { city: "Hyderabad", region: "Head Office", id: "hyderabad" },
  { city: "Delhi NCR", region: "North", id: "delhi" },
  { city: "Mumbai", region: "West", id: "mumbai" },
  { city: "Chennai", region: "South", id: "chennai" },
  { city: "Kolkata", region: "East", id: "kolkata" },
];

export const Network = () => (
  <section
    id="network"
    data-testid="network-section"
    className="scroll-mt-20 py-24 sm:py-32 bg-[#161B22] border-y border-slate-800"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
      <div>
        <SectionHead
          overline="Network"
          title="Corridors, not promises."
          sub="A pan-India lane network run from our Hyderabad head office and four regional desks, running the vehicle classes Indian industry actually ships on."
        />

        <Reveal delay={0.1} className="mt-10">
          <p className="mono text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">
            Vehicle classes handled
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {VEHICLES.map((v) => (
              <span
                key={v}
                data-testid={`vehicle-chip-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="mono text-[11px] uppercase tracking-[0.12em] px-3 py-1.5 border border-slate-700 text-slate-300 hover:border-amber-500 hover:text-amber-400 transition-colors duration-200"
              >
                {v}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-10">
          <p className="mono text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">
            Regional desks
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
            {HUBS.map((h) => (
              <div key={h.id} data-testid={`hub-${h.id}`} className="bg-[#161B22] p-5">
                <p className="font-display font-bold uppercase tracking-wide text-slate-100">
                  {h.city}
                </p>
                <p className="mono mt-1 text-[11px] uppercase tracking-[0.15em] text-amber-500/80">
                  {h.region}
                </p>
              </div>
            ))}
            <div className="bg-[#0D1117] p-5 flex items-center">
              <p className="mono text-[11px] uppercase tracking-[0.15em] text-slate-600">
                Every lane GPS-tracked, end to end
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div
          data-testid="network-map"
          className="relative border border-slate-800 bg-[#0D1117] p-4 sm:p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Pan-India corridor map
            </span>
            <span className="mono flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-emerald-400">
              <span className="w-1.5 h-1.5 bg-emerald-400 animate-pulse" /> Live lanes
            </span>
          </div>
          <svg viewBox="0 0 1486 1734" className="w-full h-auto" role="img" aria-label="Map of India showing Agaman Freight corridors connecting Hyderabad HQ, Delhi NCR, Mumbai, Chennai and Kolkata">
            <defs>
              <pattern id="netgrid" width="90" height="90" patternUnits="userSpaceOnUse">
                <path d="M90 0H0V90" fill="none" stroke="#1A222E" strokeWidth="2" />
              </pattern>
            </defs>
            <rect width="1486" height="1734" fill="url(#netgrid)" />
            <path d={INDIA_PATH} fill="#131A24" stroke="#3A4658" strokeWidth="5" strokeLinejoin="round" />
            {ROUTES.map((r, i) => (
              <path
                key={i}
                d={routePath(r)}
                fill="none"
                stroke="#F59E0B"
                strokeWidth="7"
                strokeOpacity="0.75"
                strokeLinecap="round"
                className="route-line"
              />
            ))}
            {Object.keys(CITIES).map((k) => {
              const isHub = HUBS.some((h) => h.id === k);
              const { x, y } = pos(k);
              const left = LABEL_LEFT.has(k);
              return (
                <g key={k}>
                  {isHub && (
                    <circle cx={x} cy={y} r="20" fill="none" stroke="#F59E0B" strokeWidth="3" className="hub-pulse" />
                  )}
                  <circle cx={x} cy={y} r={isHub ? 13 : 8} fill={isHub ? "#F59E0B" : "#64748B"} />
                  {isHub && (
                    <text
                      x={left ? x - 32 : x + 32}
                      y={y + 10}
                      textAnchor={left ? "end" : "start"}
                      fill="#F1F5F9"
                      fontSize="30"
                      fontFamily="JetBrains Mono, monospace"
                      letterSpacing="3"
                    >
                      {HUBS.find((h) => h.id === k).city.toUpperCase()}{k === "hyderabad" ? " · HQ" : ""}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          <p className="mono mt-4 text-[10px] uppercase tracking-[0.15em] text-slate-600">
            Hub positions geographic · lane paths indicative
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
